import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LinkIcon from '@mui/icons-material/Link';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  MeetingCard,
  CardHeader,
  AvatarGroup,
  MoreAttendeesChip,
  AttendeeNames,
  PrimaryAttendeeName,
  SecondaryAttendeeText,
  MeetingSubject,
  MeetingDescription,
  TypeBadge,
  StatusBadge,
  CardFooter,
  DateInfo,
  CountsInfo,
  CountItem,
} from '../styles';
import { formatDate } from '../../../utils/dateUtils';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import { MEETING_ATTENDEE_TYPES } from '../../../constants/meetingAttendeeTypes';

const getStatusColors = (status) => {
  const color = status?.color || '#6b7280';
  return {
    bg: `${color}15`,
    color: color,
  };
};

const getTypeColors = (type) => {
  const color = type?.color || '#6b7280';
  return {
    bg: `${color}15`,
    color: color,
    border: `${color}50`,
  };
};

const MeetingCardItem = ({ meeting, onClick, onView, onEdit, isOwner }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const attendees = meeting.attendees || [];
  const currentStatus = meeting.currentStatus;
  const meetingTypes = meeting.meetingTypes || [];
  const statusColors = getStatusColors(currentStatus);

  // Get owner and other attendees
  const owner = attendees.find(a => a.attendeeType?.code === MEETING_ATTENDEE_TYPES.OWNER);
  const otherAttendees = attendees.filter(a => a.attendeeType?.code !== MEETING_ATTENDEE_TYPES.OWNER);
  const displayedAttendees = [owner, ...otherAttendees].filter(Boolean).slice(0, 3);
  const remainingCount = attendees.length - 3;

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not scheduled';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const talkingPointsCount = meeting.talkingPoints?.length || meeting._count?.talkingPoints || 0;
  const notesCount = meeting.notes?.length || meeting._count?.notes || 0;
  const actionItemsCount = meeting.actionItems?.length || meeting._count?.actionItems || 0;
  const overdueActionItemsCount = meeting._count?.overdueActionItems || 0;
  const referencesCount = meeting.references?.length || meeting._count?.references || 0;

  return (
    <MeetingCard onClick={onClick}>
      <CardHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AvatarGroup>
            {displayedAttendees.map((attendee, idx) => (
              <EmployeeAvatar
                key={attendee.employee?.id || idx}
                employee={attendee.employee}
                size={32}
              />
            ))}
            {remainingCount > 0 && (
              <MoreAttendeesChip>+{remainingCount}</MoreAttendeesChip>
            )}
          </AvatarGroup>
          <AttendeeNames>
            <PrimaryAttendeeName>
              {owner?.employee?.fullName || 'Unknown Owner'}
            </PrimaryAttendeeName>
            {otherAttendees.length > 0 && (
              <SecondaryAttendeeText>
                with {otherAttendees.length === 1
                  ? otherAttendees[0]?.employee?.fullName
                  : `${otherAttendees.length} others`}
              </SecondaryAttendeeText>
            )}
          </AttendeeNames>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </CardHeader>

      <MeetingSubject>{meeting.subject || 'Untitled Meeting'}</MeetingSubject>

      {meeting.description && (
        <MeetingDescription>{meeting.description}</MeetingDescription>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        {meetingTypes.length > 0 && meetingTypes.map((mt, idx) => {
          const typeColors = getTypeColors(mt.meetingType);
          return (
            <TypeBadge
              key={idx}
              $bgColor={typeColors.bg}
              $color={typeColors.color}
              $borderColor={typeColors.border}
            >
              {mt.meetingType?.name || 'Unknown Type'}
            </TypeBadge>
          );
        })}
        {meetingTypes.length === 0 && (
          <TypeBadge $bgColor="#f3f4f6" $color="#6b7280" $borderColor="#e5e7eb">
            No Type
          </TypeBadge>
        )}
      </Box>

      <CardFooter>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DateInfo>
            <CalendarTodayIcon sx={{ fontSize: 16 }} />
            {formatDateTime(meeting.scheduledAt)}
          </DateInfo>
          <StatusBadge
            $bgColor={statusColors.bg}
            $color={statusColors.color}
          >
            {currentStatus?.name || 'Draft'}
          </StatusBadge>
        </Box>
        <CountsInfo>
          {talkingPointsCount > 0 && (
            <Tooltip title="Talking Points" arrow>
              <CountItem>
                <ForumOutlinedIcon sx={{ fontSize: 14 }} />
                {talkingPointsCount}
              </CountItem>
            </Tooltip>
          )}
          {notesCount > 0 && (
            <Tooltip title="Notes" arrow>
              <CountItem>
                <DescriptionOutlinedIcon sx={{ fontSize: 14 }} />
                {notesCount}
              </CountItem>
            </Tooltip>
          )}
          {actionItemsCount > 0 && (
            <Tooltip title={overdueActionItemsCount > 0 ? `Action Items (${overdueActionItemsCount} Overdue)` : "Action Items"} arrow>
              <CountItem style={overdueActionItemsCount > 0 ? { color: '#DC2626' } : undefined}>
                <PlaylistAddCheckIcon sx={{ fontSize: 14, color: overdueActionItemsCount > 0 ? '#DC2626' : 'inherit' }} />
                {actionItemsCount}
              </CountItem>
            </Tooltip>
          )}
          {referencesCount > 0 && (
            <Tooltip title="References" arrow>
              <CountItem>
                <LinkIcon sx={{ fontSize: 14 }} />
                {referencesCount}
              </CountItem>
            </Tooltip>
          )}
        </CountsInfo>
      </CardFooter>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); if (onView) onView(meeting); }}>
          View
        </MenuItem>
        {isOwner && (
          <MenuItem onClick={() => { setMenuAnchor(null); if (onEdit) onEdit(meeting); }}>
            Edit
          </MenuItem>
        )}
      </Menu>
    </MeetingCard>
  );
};

export default MeetingCardItem;
